import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import '../../components/admin/AdminLayout.css';

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const fetchLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/logs?page=${page}&limit=50`);
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  const getActionBadge = (action) => {
    const actionStyles = {
      admin_login: { bg: '#dbeafe', color: '#1d4ed8', label: 'Login' },
      user_viewed: { bg: '#f3f4f6', color: '#6b7280', label: 'Viewed' },
      user_updated: { bg: '#fef3c7', color: '#d97706', label: 'Updated' },
      user_upgraded: { bg: '#d1fae5', color: '#059669', label: 'Upgraded' },
      user_downgraded: { bg: '#fee2e2', color: '#dc2626', label: 'Downgraded' },
      user_suspended: { bg: '#fee2e2', color: '#dc2626', label: 'Suspended' },
      user_unsuspended: { bg: '#d1fae5', color: '#059669', label: 'Unsuspended' },
      user_deleted: { bg: '#fee2e2', color: '#dc2626', label: 'Deleted' },
      promoter_granted: { bg: '#fce7f3', color: '#be185d', label: 'Promoter Granted' },
      promoter_revoked: { bg: '#fef3c7', color: '#d97706', label: 'Promoter Revoked' },
      refund_issued: { bg: '#ede9fe', color: '#7c3aed', label: 'Refund' }
    };
    
    const style = actionStyles[action] || { bg: '#f3f4f6', color: '#6b7280', label: action };
    
    return (
      <span 
        className="admin-badge" 
        style={{ background: style.bg, color: style.color }}
      >
        {style.label}
      </span>
    );
  };

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return '-';
    
    return Object.entries(details)
      .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
      .join(', ');
  };

  return (
    <AdminLayout>
      <div className="admin-table-container">
        <div className="admin-table-header">
          <div className="admin-table-title">
            Activity Logs ({pagination.total})
          </div>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Audit trail of all admin actions
          </p>
        </div>

        {loading ? (
          <div className="admin-loading">Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty-icon">📋</div>
            <p>No activity logs yet</p>
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target User</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id}>
                    <td>
                      <span style={{ whiteSpace: 'nowrap' }}>
                        {new Date(log.createdAt).toLocaleDateString()}
                        <br />
                        <small style={{ color: '#6b7280' }}>
                          {new Date(log.createdAt).toLocaleTimeString()}
                        </small>
                      </span>
                    </td>
                    <td>
                      {log.admin ? (
                        <span>
                          {log.admin.firstName} {log.admin.lastName}
                          <br />
                          <small style={{ color: '#6b7280' }}>{log.admin.email}</small>
                        </span>
                      ) : (
                        <span style={{ color: '#6b7280' }}>Unknown</span>
                      )}
                    </td>
                    <td>{getActionBadge(log.action)}</td>
                    <td>
                      {log.targetUser ? (
                        <span>
                          {log.targetUser.firstName} {log.targetUser.lastName}
                          <br />
                          <small style={{ color: '#6b7280' }}>{log.targetUser.email}</small>
                        </span>
                      ) : (
                        <span style={{ color: '#6b7280' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span style={{ 
                        color: '#6b7280', 
                        maxWidth: '250px', 
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.85rem'
                      }}>
                        {formatDetails(log.details)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="admin-pagination">
              <button
                className="admin-pagination-btn"
                disabled={pagination.page === 1}
                onClick={() => fetchLogs(pagination.page - 1)}
              >
                Previous
              </button>
              <span className="admin-pagination-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                className="admin-pagination-btn"
                disabled={pagination.page === pagination.pages}
                onClick={() => fetchLogs(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLogs;
